import classnames from 'classnames/bind'
import Pagination from 'entities/pagination'
import { range } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import styles from './pagination.module.scss'

const cx = classnames.bind(styles)
const PADDING = 3

const FirstItem = ({ onClick }) => (
  <li className={styles.item}>
    <button type="button" onClick={() => onClick(1)}>
      &laquo;
    </button>
  </li>
)

FirstItem.propTypes = {
  onClick: PropTypes.func.isRequired
}

const LastItem = ({ onClick, totalPages }) => (
  <li className={styles.item}>
    <button type="button" onClick={() => onClick(totalPages)}>
      &raquo;
    </button>
  </li>
)

LastItem.propTypes = {
  onClick: PropTypes.func.isRequired,
  totalPages: PropTypes.number.isRequired
}

const PrevItem = ({ prevPage, disabled, onClick }) => (
  <li className={cx(styles.item, { disabled })}>
    {disabled ? (
      <span>
        <i className="ic-chevron-left" />
      </span>
    ) : (
      <button type="button" onClick={() => onClick(prevPage)} disabled={disabled}>
        <i className="ic-chevron-left" />
      </button>
    )}
  </li>
)

PrevItem.propTypes = {
  prevPage: PropTypes.number.isRequired,
  disabled: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
}

const NextItem = ({ nextPage, disabled, onClick }) => (
  <li className={cx(styles.item, { disabled })}>
    {disabled ? (
      <span>
        <i className="ic-chevron-right" />
      </span>
    ) : (
      <button type="button" onClick={() => onClick(nextPage)} disabled={disabled}>
        <i className="ic-chevron-right" />
      </button>
    )}
  </li>
)

NextItem.propTypes = {
  nextPage: PropTypes.number.isRequired,
  disabled: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
}

const Gap = () => (
  <li className={styles.item}>
    <span>&hellip;</span>
  </li>
)

const Paginate = ({ pagination, onPaginationClick, showFirstAndLast }) => {
  const { currentPage, totalPages, totalCount } = pagination

  if (totalCount === 0) {
    return null
  }

  const firstPage = showFirstAndLast && currentPage > 1 ? <FirstItem onClick={onPaginationClick} /> : null

  const lastPage =
    showFirstAndLast && currentPage !== totalPages ? (
      <LastItem onClick={onPaginationClick} totalPages={totalPages} />
    ) : null

  const prevPage = <PrevItem onClick={onPaginationClick} prevPage={currentPage - 1} disabled={currentPage <= 1} />

  const nextPage = (
    <NextItem onClick={onPaginationClick} nextPage={currentPage + 1} disabled={currentPage + 1 > totalPages} />
  )

  const pages = [
    ...range(currentPage - PADDING, currentPage).filter(p => p >= 1),
    ...range(currentPage, currentPage + PADDING + 1).filter(p => p <= totalPages)
  ].map(p => {
    const isCurrent = p === currentPage
    const link = isCurrent ? (
      <span>{p}</span>
    ) : (
      <button type="button" onClick={() => onPaginationClick(p)}>
        {p}
      </button>
    )
    return (
      <li key={`${p}`} className={cx(styles.item, { isCurrent })}>
        {link}
      </li>
    )
  })

  const leftEllipsis = currentPage > PADDING + 1 ? <Gap /> : null
  const rightEllipsis = currentPage + PADDING < totalPages ? <Gap /> : null

  return (
    <ul className={styles.pagination}>
      {firstPage}
      {prevPage}
      {leftEllipsis}
      {pages}
      {rightEllipsis}
      {nextPage}
      {lastPage}
    </ul>
  )
}

Paginate.propTypes = {
  onPaginationClick: PropTypes.func.isRequired,
  pagination: PropTypes.instanceOf(Pagination).isRequired,
  showFirstAndLast: PropTypes.bool
}

Paginate.defaultProps = {
  showFirstAndLast: false
}

export default Paginate
